import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { formatCurrencyAmount } from "../utils/formatCurrencyAmount";
import { causesFor } from "../utils/reconciliationCauses";
import ReconciliationRecords from "./ReconciliationRecords";
import MoneyTrail from "./MoneyTrail";

const signed = (value, currency) => {
  const num = Number(value);
  if (!num) return formatCurrencyAmount(0, currency);
  return `${num > 0 ? "+" : "-"}${formatCurrencyAmount(Math.abs(num), currency)}`;
};

// Everything that explains one currency's gap, shown when its row is opened:
// how the gap splits up, each cause with its records one click away, and what
// still needs a person.
function CurrencyGapPanel({ row, filtered, since, notes }) {
  const [openKind, setOpenKind] = useState(null);
  const currency = row.currency;
  const causes = causesFor(row);
  const groups = [...new Set(causes.map((c) => c.group))];

  return (
    <div className="px-4 py-4 space-y-4 bg-gray-50/70">
      <div className="grid gap-2 sm:grid-cols-4">
        {[
          ["Gap", row.gap, "Live balance difference: house merchant minus consumers."],
          ["Explained by movements", row.explainedGap, "Movements that never reached the house merchant, plus fee differences and conversion drift."],
          ["Known history and rewards", filtered ? null : row.leftovers?.effect, "Conversions before 4 Oct and rewards Novacrust funded."],
          ["Still unknown", filtered ? null : row.remainingGap, "What is left. Fees and spread Novacrust keeps are still inside it."],
        ].map(([label, value, hint]) => (
          <div key={label} className="p-3 bg-white rounded border border-gray-200" title={hint}>
            <p className="text-xs text-gray-500">{label}</p>
            <p className={`text-base font-semibold ${value === null ? "text-gray-400" : Number(value) < 0 ? "text-red-600" : "text-gray-900"}`}>
              {value === null ? "-" : signed(value, currency)}
            </p>
          </div>
        ))}
      </div>
      {filtered && (
        <p className="text-xs text-gray-500">
          A date is set, so the all-time rows (history and rewards) and Still unknown are hidden: they cannot be dated.
        </p>
      )}

      <MoneyTrail currency={currency} />

      {groups.map((group) => (
        <div key={group}>
          <p className="mb-1 text-xs font-bold tracking-wider text-gray-500 uppercase">{group}</p>
          <div className="bg-white rounded border border-gray-200 divide-y divide-gray-100">
            {causes
              .filter((c) => c.group === group)
              .map((c) => {
                const unavailable = filtered && c.allTime;
                const has = !unavailable && c.count > 0;
                const open = openKind === c.kind;
                return (
                  <div key={c.kind}>
                    <button
                      type="button"
                      disabled={!has}
                      onClick={() => setOpenKind(open ? null : c.kind)}
                      aria-expanded={open}
                      className={`flex gap-2 items-center py-2.5 px-3 w-full text-sm text-left ${has ? "hover:bg-gray-50" : "cursor-default"}`}
                    >
                      {has ? (
                        open ? <ChevronDown className="w-4 h-4 shrink-0 text-[#013941]" /> : <ChevronRight className="w-4 h-4 shrink-0 text-[#013941]" />
                      ) : (
                        <span className="w-4 shrink-0" />
                      )}
                      <span className={`flex-1 ${has ? "font-medium text-gray-900" : "text-gray-400"}`} title={c.note}>
                        {c.label}
                        {c.summary && !unavailable && <span className="ml-2 text-xs font-normal text-gray-500">{c.summary}</span>}
                      </span>
                      <span className={`text-xs ${has ? "text-gray-600" : "text-gray-400"}`}>
                        {unavailable ? "not available with a date set" : has ? `${c.count} record${c.count === 1 ? "" : "s"}` : "none"}
                      </span>
                      <span className={`w-40 text-right ${has ? "text-gray-900" : "text-gray-300"}`}>
                        {has ? formatCurrencyAmount(c.amount, currency) : "-"}
                      </span>
                    </button>
                    {open && (
                      <div className="px-3 pl-9 border-t border-gray-100">
                        <ReconciliationRecords kind={c.kind} currency={currency} since={since} />
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      ))}

      {notes.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-bold tracking-wider text-gray-500 uppercase">To investigate</p>
          <ul className="space-y-1">
            {notes.map((n) => (
              <li key={n.key} className="text-sm text-gray-700">
                {n.text}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default CurrencyGapPanel;
